import { getQuestionTypeMeta } from '@/features/shared/types/question';
import type { PracticeQuestionNavigationContext, PracticeQuestionNavigationItem } from '../types/practice';
import type { PracticeQuestionNavigationSummary } from '../types/practice';
import { resolveQuestionDisplayId, resolveQuestionTitle } from './questionDisplay';

const SECTION_LABELS: Record<string, string> = {
  speaking: 'Speaking',
  writing: 'Writing',
  reading: 'Reading',
  listening: 'Listening',
};

export interface PracticeNavigationTreeItem extends PracticeQuestionNavigationItem {
  overallIndex: number;
  typeIndex: number;
  displayId: string;
}

export interface PracticeNavigationTreeGroup {
  questionTypeCode: string;
  typeName: string;
  isCurrentType: boolean;
  completedCount: number;
  totalCount: number;
  items: PracticeNavigationTreeItem[];
}

export interface PracticeNavigationTreeSection {
  sectionKey: string;
  sectionLabel: string;
  isCurrentSection: boolean;
  completedCount: number;
  totalCount: number;
  typeGroups: PracticeNavigationTreeGroup[];
}

export function buildPracticeNavigationSections(
  navigationContext: PracticeQuestionNavigationContext | null | undefined,
  currentQuestionTypeCode: string | null | undefined,
  options?: { preserveEmptyCurrentType?: boolean },
): PracticeNavigationTreeSection[] {
  if (!navigationContext?.items?.length) {
    if (!navigationContext || !options?.preserveEmptyCurrentType) {
      return [];
    }
    const questionTypeCode = navigationContext.questionTypeCode || currentQuestionTypeCode;
    if (!questionTypeCode) {
      return [];
    }
    const itemMeta = getQuestionTypeMeta(questionTypeCode);
    const sectionKey = itemMeta.section || 'speaking';
    const totalCount = Math.max(0, navigationContext.totalCount || 0);
    return [{
      sectionKey,
      sectionLabel: SECTION_LABELS[sectionKey] || sectionKey,
      isCurrentSection: true,
      completedCount: 0,
      totalCount,
      typeGroups: [{
        questionTypeCode,
        typeName: itemMeta.nameEn,
        isCurrentType: true,
        completedCount: 0,
        totalCount,
        items: [],
      }],
    }];
  }
  const sectionOrder = ['speaking', 'writing', 'reading', 'listening'];
  const grouped = new Map(
    sectionOrder.map((sectionKey) => [
      sectionKey,
      {
        sectionKey,
        sectionLabel: SECTION_LABELS[sectionKey],
        isCurrentSection: false,
        completedCount: 0,
        totalCount: 0,
        typeGroups: [] as PracticeNavigationTreeGroup[],
      },
    ]),
  );

  navigationContext.items.forEach((item, index) => {
    const itemMeta = getQuestionTypeMeta(item.questionTypeCode || navigationContext.questionTypeCode || '');
    const sectionKey = itemMeta.section || 'speaking';
    const section = grouped.get(sectionKey);
    if (!section) {
      return;
    }
    let typeGroup = section.typeGroups.find((entry) => entry.questionTypeCode === item.questionTypeCode);
    if (!typeGroup) {
      typeGroup = {
        questionTypeCode: item.questionTypeCode,
        typeName: itemMeta.nameEn,
        isCurrentType: item.questionTypeCode === currentQuestionTypeCode,
        completedCount: 0,
        totalCount: 0,
        items: [],
      };
      section.typeGroups.push(typeGroup);
    }
    typeGroup.isCurrentType = typeGroup.isCurrentType || item.questionTypeCode === currentQuestionTypeCode;
    const typeIndex = typeGroup.items.length + 1;
    typeGroup.items.push({
      ...item,
      overallIndex: index + 1,
      typeIndex,
      title: resolveQuestionTitle({
        mode: item.titleDisplayMode,
        title: item.title,
        questionTypeCode: item.questionTypeCode,
        legacyQuestionId: item.legacyQuestionId,
        legacyQIdx: item.legacyQIdx,
        sequence: typeIndex,
      }),
      displayId: resolveQuestionDisplayId(item.questionTypeCode, item.legacyQuestionId, item.legacyQIdx, typeIndex),
    });
    typeGroup.totalCount = typeGroup.items.length;
    typeGroup.completedCount = typeGroup.items.filter((entry) => entry.progressStatus === 'done').length;
  });

  return sectionOrder
    .map((sectionKey) => grouped.get(sectionKey))
    .filter((section): section is PracticeNavigationTreeSection => Boolean(section && section.typeGroups.length))
    .map((section) => {
      const completedCount = section.typeGroups.reduce((sum, group) => sum + group.completedCount, 0);
      const totalCount = section.typeGroups.reduce((sum, group) => sum + group.totalCount, 0);
      return {
        ...section,
        isCurrentSection: section.typeGroups.some((group) => group.isCurrentType),
        completedCount,
        totalCount,
      };
    });
}

export function buildPracticeNavigationLabel(navigationContext: PracticeQuestionNavigationContext | null | undefined) {
  if (!navigationContext) {
    return 'Question navigation';
  }
  return navigationContext.contextType === 'question_set'
    ? navigationContext.questionSetName || 'Current question set'
    : 'Question navigation';
}

export function reindexCurrentTypeNavigation(
  sections: PracticeNavigationTreeSection[],
  currentQuestionId: string | null | undefined,
  currentTypeIndex: number,
) {
  if (!currentQuestionId || currentTypeIndex <= 0) {
    return sections;
  }
  return sections.map((section) => ({
    ...section,
    typeGroups: section.typeGroups.map((group) => {
      if (!group.items.some((item) => item.questionId === currentQuestionId)) {
        return group;
      }
      let nonCurrentCount = 0;
      const reindexedItems = group.items.map((item) => {
        let typeIndex: number;
        if (item.questionId === currentQuestionId) {
          typeIndex = currentTypeIndex;
        } else {
          nonCurrentCount += 1;
          typeIndex = nonCurrentCount >= currentTypeIndex ? nonCurrentCount + 1 : nonCurrentCount;
        }
        return {
          ...item,
          typeIndex,
          title: resolveQuestionTitle({
            mode: item.titleDisplayMode,
            title: item.title,
            questionTypeCode: item.questionTypeCode,
            legacyQuestionId: item.legacyQuestionId,
            legacyQIdx: item.legacyQIdx,
            sequence: typeIndex,
          }),
          displayId: resolveQuestionDisplayId(
            item.questionTypeCode,
            item.legacyQuestionId,
            item.legacyQIdx,
            typeIndex,
          ),
        };
      });
      return {
        ...group,
        items: reindexedItems.sort((left, right) => left.typeIndex - right.typeIndex),
      };
    }),
  }));
}

export function buildPracticeNavigationSummarySections(
  summaries: PracticeQuestionNavigationSummary[],
  currentQuestionTypeCode: string | null | undefined,
): PracticeNavigationTreeSection[] {
  const sections = new Map<string, PracticeNavigationTreeSection>();
  for (const summary of summaries) {
    const sectionKey = summary.section || 'speaking';
    const section = sections.get(sectionKey) || {
      sectionKey,
      sectionLabel: SECTION_LABELS[sectionKey] || sectionKey,
      isCurrentSection: false,
      completedCount: 0,
      totalCount: 0,
      typeGroups: [],
    };
    const isCurrent = summary.questionTypeCode === currentQuestionTypeCode;
    section.typeGroups.push({
      questionTypeCode: summary.questionTypeCode,
      typeName: summary.nameEn,
      isCurrentType: isCurrent,
      completedCount: summary.completedCount,
      totalCount: summary.totalCount,
      items: [],
    });
    section.completedCount += summary.completedCount;
    section.totalCount += summary.totalCount;
    section.isCurrentSection = section.isCurrentSection || isCurrent;
    sections.set(sectionKey, section);
  }
  return ['speaking', 'writing', 'reading', 'listening']
    .map((key) => sections.get(key))
    .filter((section): section is PracticeNavigationTreeSection => Boolean(section));
}
